// Duels: challenges to and from friends and server mates, and the friend link.
import type { DuelChallenge, DuelPage, DuelSent, DuelView, LineupPick, MatchStarted, Ok, Player } from '../src/api.js';
import { match } from 'ts-pattern';
import { Lineups } from './lineups.js';
import { Page } from './page.js';
import { Session } from './session.js';
import { type Html, Ui } from './ui.js';

const { api, h, setHtml, el, elOf, toast, attempt } = Ui;

const layoutHtml = h`<div id="duels"><h2>Duel</h2>
    <p class="muted">Challenge a friend or anyone you share a server with. They have a minute to accept, and the fight
      starts right after. Duels count for nothing but bragging rights.</p>
    <div class="stack">
      <div class="stack" id="duelNews"></div>
      <section class="panel stack" id="duelLineup"></section>
      <section class="stack">
        <h3>Opponents</h3>
        <input class="field" id="opponentSearch" type="search" placeholder="Search by name" aria-label="Search opponents">
        <div class="list" id="opponents"></div>
      </section>
      <section class="panel stack" id="friendLink"></section>
    </div></div>`;

const answerCountdownHtml = (d: DuelView): Html =>
  h`<div class="muted">Answer within <span class="countdown inline" data-until="${d.answerUntil}"></span></div>`;

const upcomingHtml = (d: DuelView): Html =>
  h`<section class="panel stack"><div class="row tight heading-row"><h3>You vs ${d.opponent.name}</h3>
      ${
        (d.startAt ?? 0) > Session.serverNow()
          ? h`<div class="muted">Starts in <span class="countdown inline" data-until="${d.startAt ?? 0}"></span></div>`
          : h`<div class="status-live">Live now</div>`
      }</div>
      <div><a class="btn small" href="/match.html?id=${d.matchId ?? ''}">Watch</a></div></section>`;

const incomingHtml = (player: Player, d: DuelView): Html =>
  h`<section class="panel stack duel-call"><div class="row tight heading-row">
        <h3>${Page.avatarHtml(d.opponent.avatar)} ${d.opponent.name} challenges you</h3>
        ${answerCountdownHtml(d)}</div>
      <p class="muted flush">You fight with ${Session.activeLineup(player)?.name ?? 'your lineup'}. Their lineup stays hidden until the fight.</p>
      <div class="actions"><button class="btn small" data-accept="${d.id}" ${Session.hasPlayableLineup(player) ? '' : 'disabled'}>Accept</button>
        <button class="btn ghost small" data-decline="${d.id}">Decline</button></div></section>`;

const sentHtml = (d: DuelView): Html =>
  match(d.status)
    .with(
      'open',
      () => h`<section class="panel stack"><div class="row tight heading-row"><h3>Waiting for ${d.opponent.name}</h3>
          ${answerCountdownHtml(d)}</div>
          <div><button class="btn ghost small" data-cancel="${d.id}">Withdraw</button></div></section>`,
    )
    .with(
      'declined',
      () => h`<section class="panel"><p class="muted flush">${d.opponent.name} declined your challenge.</p></section>`,
    )
    .otherwise(
      () => h`<section class="panel"><p class="muted flush">${d.opponent.name} didn't answer in time.</p></section>`,
    );

const newsHtml = (player: Player, page: DuelPage): Html =>
  h`${page.upcoming.map(upcomingHtml)}${page.incoming.map(d => incomingHtml(player, d))}${page.sent ? sentHtml(page.sent) : ''}`;

const lineupHtml = (player: Player): Html =>
  h`<h3>Your lineup</h3>
      <p class="muted flush">You fight with it when you challenge or accept, and it's locked in once you do.</p>
      ${Lineups.pickerHtml(player)}`;

/** Accept when they've challenged you already, Challenge otherwise. */
const opponentActionHtml = (player: Player, page: DuelPage, id: string): Html => {
  const theirs = page.incoming.find(d => d.opponent.id === id);
  const off = !Session.hasPlayableLineup(player) || (!theirs && page.sent?.status === 'open');
  return theirs
    ? h`<button class="btn small" data-accept="${theirs.id}" ${off ? 'disabled' : ''}>Accept</button>`
    : h`<button class="btn small" data-challenge="${id}" ${off ? 'disabled' : ''}>Challenge</button>`;
};

const opponentsHtml = (player: Player, page: DuelPage, query: string): Html => {
  const shown = page.opponents.filter(o => o.name.toLowerCase().includes(query));
  if (!shown.length)
    return h`<p class="muted">${page.opponents.length ? 'Nobody by that name.' : 'Nobody to challenge yet. Send a friend your link.'}</p>`;
  return h`${shown.map(
    o => h`<div class="list-item">${Page.avatarHtml(o.avatar)}<div class="grow">${o.name}${o.friend ? h` <span class="in-tag">Friend</span>` : ''}
        ${player.servers.length > 1 && o.servers.length ? h`<div class="muted sub">${o.servers.join(' · ')}</div>` : ''}</div>
        ${o.friend ? h`<button class="link-btn" data-unfriend="${o.id}" data-name="${o.name}">Unfriend</button>` : ''}
        ${opponentActionHtml(player, page, o.id)}</div>`,
  )}`;
};

const friendLinkHtml = (page: DuelPage): Html =>
  h`<h3>Friend link</h3>
      <p class="muted flush">Whoever opens it becomes your friend, so you can duel without sharing a server. They don't
        need a Shobu Arena server to log in. It works until ${Page.formatDate(page.inviteLink.expiresAt)}, then you get a new one.</p>
      <div class="invite-link"><input class="field" id="inviteLink" readonly value="${page.inviteLink.url}" aria-label="Your friend link">
        <button class="btn ghost small" id="copyInvite">Copy</button></div>`;

const copyInviteLink = async (): Promise<void> => {
  await navigator.clipboard.writeText(elOf(HTMLInputElement, '#inviteLink').value);
  toast('Friend link copied');
};

const view = async (player: Player): Promise<void> => {
  setHtml(Page.view, layoutHtml);
  const root = el('#duels');
  const search = elOf(HTMLInputElement, '#opponentSearch');
  let page: DuelPage | null = null;
  const tick = Page.countdown('[data-until]', e => Number(e.dataset.until));

  const draw = () => {
    if (!page) return;
    const newsChanged = Page.renderIfChanged(el('#duelNews'), newsHtml(player, page));
    Page.renderIfChanged(el('#duelLineup'), lineupHtml(player));
    Page.renderIfChanged(el('#opponents'), opponentsHtml(player, page, search.value.trim().toLowerCase()));
    if (Page.renderIfChanged(el('#friendLink'), friendLinkHtml(page)))
      el('#copyInvite').onclick = () => attempt(copyInviteLink);
    if (newsChanged) tick();
  };

  /** Opens the fight straight away once the opponent accepts your challenge. */
  const load = async () => {
    const waitingOn = page?.sent?.status === 'open' ? page.sent.id : null;
    page = await api<DuelPage>('/api/duels');
    Session.setBadges({ incomingDuels: page.incoming.length });
    const accepted = page.upcoming.find(d => d.id === waitingOn);
    if (accepted?.matchId) Page.openMatch({ matchId: accepted.matchId });
    else draw();
  };

  const lineupPick = (): LineupPick => ({ lineupId: Session.activeLineup(player)?.id });

  const challenge = async (opponentId: string) => {
    const body: DuelChallenge = { opponentId, ...lineupPick() };
    const sent = await api<DuelSent>('/api/duels', { method: 'POST', body });
    if (sent.matchId) Page.openMatch({ matchId: sent.matchId });
    else await load();
  };
  const accept = async (duelId: string) =>
    Page.openMatch(await api<MatchStarted>(`/api/duels/${duelId}/accept`, { method: 'POST', body: lineupPick() }));
  const answer = async (duelId: string, verb: 'decline' | 'cancel') => {
    await api<Ok>(`/api/duels/${duelId}/${verb}`, { method: 'POST' });
    await load();
  };
  const unfriend = async (playerId: string) => {
    await api<Ok>(`/api/friends/${encodeURIComponent(playerId)}`, { method: 'DELETE' });
    await load();
  };

  root.onclick = e => {
    const btn = e.target instanceof Element ? e.target.closest<HTMLButtonElement>('button') : null;
    if (!btn) return;
    const { lineup, challenge: opponentId, accept: acceptId, decline, cancel, unfriend: friendId, name } = btn.dataset;
    if (lineup) {
      Session.setActiveLineup(Number(lineup));
      draw();
    } else if (opponentId) void Page.busy(btn, () => challenge(opponentId));
    else if (acceptId) void Page.busy(btn, () => accept(acceptId));
    else if (decline) void Page.busy(btn, () => answer(decline, 'decline'));
    else if (cancel) void Page.busy(btn, () => answer(cancel, 'cancel'));
    else if (friendId && confirm(`Unfriend ${name ?? ''}?`)) void Page.busy(btn, () => unfriend(friendId));
  };
  search.oninput = draw;

  await load();
  Page.poll(load, 3000);
};

export const Duels = { view };
